import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { api } from '../lib/api'
import { useAsync } from '../lib/useAsync'
import type { SortKey } from '../lib/types'
import { TheoryCard } from '../components/TheoryCard'
import { Icon } from '../components/Icon'
import { EmptyState, ErrorState, Loading } from '../components/States'

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'credibility', label: 'Most credible' },
  { value: 'unhinged', label: 'Most unhinged' },
  { value: 'popular', label: 'Most read' },
  { value: 'longest', label: 'Longest' },
  { value: 'title', label: 'A–Z' },
]

export function Archive() {
  const [params, setParams] = useSearchParams()

  const tag = params.get('tag') ?? ''
  const sort = (params.get('sort') as SortKey) ?? 'newest'
  const searchParam = params.get('search') ?? ''

  // Local mirror keeps typing responsive; the URL updates on a debounce so the
  // filter state stays shareable and survives back/forward.
  const [searchInput, setSearchInput] = useState(searchParam)
  useEffect(() => setSearchInput(searchParam), [searchParam])

  useEffect(() => {
    if (searchInput === searchParam) return
    const timer = setTimeout(() => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (searchInput) next.set('search', searchInput)
          else next.delete('search')
          return next
        },
        { replace: true },
      )
    }, 250)
    return () => clearTimeout(timer)
  }, [searchInput, searchParam, setParams])

  const feed = useAsync(
    () => api.theories({ search: searchParam, tag, sort, limit: 200 }),
    [searchParam, tag, sort],
  )
  const tagList = useAsync(() => api.tags(), [])
  const site = useAsync(() => api.site(), [])
  // Chips that all read "1" make the archive look emptier than a plain list
  // would. Switchable from the admin rather than hidden below a hardcoded count.
  const showFilters = site.data?.display?.filters !== false

  const update = (key: string, value: string | null) => {
    setParams((current) => {
      const next = new URLSearchParams(current)
      if (value) next.set(key, value)
      else next.delete(key)
      return next
    })
  }

  const theories = feed.data?.theories ?? []
  const hasFilters = Boolean(searchParam || tag || sort !== 'newest')

  return (
    <div className="page section">
      <header className="archive-head">
        <div>
          <p className="eyebrow">The archive</p>
          <h1 className="page-title">Every theory, on the record</h1>
        </div>
        <p className="lede">
          Sorted, searchable, and preserved exactly as stated. Corrections are not accepted.
        </p>
      </header>

      <div className="filters" role="search">
        <div className="search-field">
          <Icon name="search" size={18} />
          <input
            type="search"
            className="input"
            placeholder="Search theories…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Search theories"
          />
        </div>

        <div className="filter-row">
          <label className="filter-select">
            <span className="visually-hidden">Sort by</span>
            <select
              className="select"
              value={sort}
              onChange={(event) => update('sort', event.target.value)}
            >
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {hasFilters && (
            <button type="button" className="btn btn-subtle btn-sm" onClick={() => setParams({})}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {showFilters && tagList.data && tagList.data.tags.length > 0 && (
        <ul className="tag-rail" aria-label="Filter by tag">
          <li>
            <button
              type="button"
              className="chip"
              aria-pressed={!tag}
              onClick={() => update('tag', null)}
            >
              All
            </button>
          </li>
          {tagList.data.tags.map((item) => (
            <li key={item.name}>
              <button
                type="button"
                className="chip"
                aria-pressed={tag === item.name}
                onClick={() => update('tag', tag === item.name ? null : item.name)}
              >
                {item.name}
                <span className="chip-count">{item.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {feed.loading && <Loading label="Searching the archive…" />}
      {feed.error && <ErrorState message={feed.error} onRetry={feed.reload} />}

      {!feed.loading && !feed.error && (
        <>
          <p className="result-count mono" role="status">
            {feed.data?.total ?? 0} {feed.data?.total === 1 ? 'theory' : 'theories'}
            {hasFilters ? ' matching' : ' on file'}
          </p>

          {theories.length === 0 ? (
            <EmptyState title="No theories match that">
              Try a looser search, or{' '}
              <button type="button" className="link-button" onClick={() => setParams({})}>
                clear the filters
              </button>
              .
            </EmptyState>
          ) : (
            <div className="theory-grid">
              {theories.map((theory) => (
                <TheoryCard
                  key={theory.id}
                  theory={theory}
                  // Only the ones with audio can be queued up after this one.
                  queue={theories.filter((item) => item.audio)}
                  featured={theory.featured}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
